import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import WorkOutlineIcon from '@mui/icons-material/WorkOutline'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import PsychologyIcon from '@mui/icons-material/Psychology'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { validateEmail } from 'data/helpers'
import { fetchCreateContactEmail } from 'src/redux/contact'
import treasureMapImage from 'static/images/aappr/treasure-map.jpg'

const CANDIDATE_SEARCHES = [
  'Family Medicine physician seeking roles in Texas',
  'CRNA open to relocation in the Southeast',
  'Hospitalist looking for academic positions',
  'Nurse Practitioner seeking primary care in the Midwest',
  'Orthopedic Surgeon exploring group practice opportunities',
  'Certified Nurse-Midwife open to rural settings',
  'Emergency Medicine physician seeking West Coast positions',
  'Physician Assistant looking for urgent care roles',
]

const HOW_IT_WORKS_STEPS = [
  {
    icon: WorkOutlineIcon,
    title: 'Post Your Position',
    description: 'Describe what you\'re looking for — specialty, location, practice setting.',
  },
  {
    icon: PeopleOutlineIcon,
    title: 'See Candidate Intent',
    description:
      'See what candidates are actively searching for. Our platform automatically matches you with high-intent clinicians.',
  },
  {
    icon: ConnectWithoutContactIcon,
    title: 'Connect Directly',
    description: 'No recruiter middlemen. Reach out to candidates who are already interested.',
  },
]

const WHY_MYSTETHI_FEATURES = [
  {
    icon: LocalHospitalIcon,
    title: 'Built by Physicians',
    description: 'We understand healthcare recruitment because we\'ve lived it.',
  },
  {
    icon: PsychologyIcon,
    title: 'AI-Powered Matching',
    description:
      'Our AI assistant Stethi helps candidates find the right fit, meaning you get higher-intent applicants.',
  },
  {
    icon: VerifiedUserIcon,
    title: 'No Spam, No Games',
    description: 'Clean listings, verified candidates, direct contact info.',
  },
  {
    icon: TrendingUpIcon,
    title: 'Real-Time Search Insights',
    description:
      'See exactly what candidates are looking for — specialty, location, practice type — so you can recruit with intent, not guesswork.',
  },
]

const INITIAL_FORM_VALUES = {
  firstName: '',
  lastName: '',
  email: '',
  organization: '',
  message: '',
}

const REQUIRED_FIELDS = ['firstName', 'lastName', 'email', 'organization']

const FIELD_LABELS = {
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email',
  organization: 'Organization',
  message: 'Message',
}

function validateField(name, value) {
  if (REQUIRED_FIELDS.includes(name) && !value.trim()) {
    return `${FIELD_LABELS[name]} is required.`
  }
  if (name === 'email' && value.trim() && !validateEmail(value)) {
    return 'Please enter a valid email address.'
  }
  return null
}

function IconCircle({ icon: Icon, theme }) {
  return (
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: '50%',
        backgroundColor: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.common.white,
        mb: 2,
      }}
    >
      <Icon />
    </Box>
  )
}

function HeroSection({ theme, onScheduleDemo }) {
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.dark,
        backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${treasureMapImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        color: theme.palette.common.white,
        py: { xs: 6, md: 10 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth='lg'>
        <Typography
          variant='overline'
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 600,
            letterSpacing: '0.1em',
            fontSize: '0.85rem',
            display: 'block',
            mb: 1,
          }}
        >
          Advancing Connections 2026 | April 13-15, Orlando FL
        </Typography>

        <Box
          sx={{
            display: 'inline-block',
            backgroundColor: theme.palette.primary.main,
            borderRadius: '12px',
            px: 3,
            py: 1.5,
            mb: 3,
          }}
        >
          <Typography
            variant='h4'
            sx={{
              fontWeight: 800,
              color: theme.palette.common.white,
              fontSize: { xs: '1.5rem', md: '2rem' },
              lineHeight: 1.2,
            }}
          >
            Visit us at Booth 525
          </Typography>
        </Box>

        <Typography
          variant='h2'
          sx={{
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '3rem' },
            lineHeight: 1.2,
            mb: 3,
            color: theme.palette.common.white,
          }}
        >
          Trying to fill a position?
          <br />
          Your next hire is closer than you think.
        </Typography>

        <Button
          variant='contained'
          size='large'
          onClick={onScheduleDemo}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            fontWeight: 600,
            px: 4,
            py: 1.5,
            fontSize: '1rem',
            textTransform: 'none',
            borderRadius: '8px',
            boxShadow: theme.palette.shadows.colored.primary,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
              boxShadow: theme.palette.shadows.colored.primaryHover,
              transform: 'translateY(-2px)',
            },
          }}
        >
          Schedule a Demo
        </Button>
      </Container>
    </Box>
  )
}

function TreasureChestExplainer({ theme, onScheduleDemo }) {
  return (
    <Box sx={{ backgroundColor: theme.palette.common.white, py: { xs: 6, md: 8 } }}>
      <Container maxWidth='lg'>
        <Box
          sx={{
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            backgroundColor: `${theme.palette.primary.main}08`,
            borderRadius: '16px',
            p: { xs: 3, md: 5 },
          }}
        >
          <Typography
            variant='h3'
            sx={{
              fontWeight: 700,
              color: theme.palette.grey[900],
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              mb: 2,
            }}
          >
            Found a Treasure Chest?
          </Typography>
          <Typography
            variant='body1'
            sx={{
              color: theme.palette.grey[600],
              fontSize: '1.05rem',
              lineHeight: 1.7,
              mb: 3,
            }}
          >
            Each treasure chest contains a real candidate search from a clinician actively looking on
            MyStethi. The search you found — whether it's Neurology in California or Family Medicine
            in Texas — represents a real person ready to connect. We have hundreds more. Schedule a
            demo to see the matches for your open positions, or{' '}
            <Box component='span' sx={{ fontWeight: 700 }}>
              stop by Booth 525.
            </Box>
          </Typography>
          <Button
            variant='contained'
            size='large'
            onClick={onScheduleDemo}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              fontWeight: 600,
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              textTransform: 'none',
              borderRadius: '8px',
              boxShadow: theme.palette.shadows.colored.primary,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: theme.palette.shadows.colored.primaryHover,
                transform: 'translateY(-2px)',
              },
            }}
          >
            Schedule a Demo
          </Button>
        </Box>
      </Container>
    </Box>
  )
}

function ScheduleDemo({ theme, formRef }) {
  return (
    <Box ref={formRef} sx={{ backgroundColor: theme.palette.common.white, py: { xs: 6, md: 8 } }}>
      <Container maxWidth='lg'>
        <Grid container spacing={6} alignItems='flex-start'>
          <Grid item xs={12} md={5}>
            <Typography
              variant='overline'
              sx={{
                color: theme.palette.primary.dark,
                fontWeight: 600,
                letterSpacing: '0.1em',
                display: 'block',
                mb: 1,
              }}
            >
              Get Started
            </Typography>
            <Typography
              variant='h3'
              sx={{
                fontWeight: 700,
                color: theme.palette.grey[900],
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                mb: 2,
              }}
            >
              See It in Action
            </Typography>
            <Box
              component='ul'
              sx={{
                listStyle: 'none',
                p: 0,
                m: 0,
                mb: 3,
              }}
            >
              {[
                'See real-time candidate search activity',
                'Get matched with high-intent clinicians',
                'Learn about exclusive AAPPR attendee pricing',
              ].map(item => (
                <Box
                  component='li'
                  key={item}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 0.75,
                    fontSize: '1rem',
                    color: theme.palette.grey[700],
                    '&::before': {
                      content: '"•"',
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                      fontSize: '1.25rem',
                      lineHeight: 1,
                      flexShrink: 0,
                    },
                  }}
                >
                  {item}
                </Box>
              ))}
            </Box>
            <Typography
              variant='body1'
              sx={{
                color: theme.palette.grey[600],
                fontSize: '1.05rem',
                lineHeight: 1.7,
              }}
            >
              See how MyStethi can transform your recruitment. Fill out the form and we'll set up a
              personalized demo.
            </Typography>
          </Grid>
          <Grid item xs={12} md={7}>
            <ContactForm theme={theme} />
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

function CandidateSearchIntelligence({ theme, onScheduleDemo }) {
  return (
    <Box sx={{ backgroundColor: theme.palette.background.paper, py: { xs: 6, md: 8 } }}>
      <Container maxWidth='lg'>
        <Typography
          variant='overline'
          sx={{
            color: theme.palette.primary.dark,
            fontWeight: 600,
            letterSpacing: '0.1em',
            display: 'block',
            mb: 1,
          }}
        >
          The MyStethi Advantage
        </Typography>
        <Typography
          variant='h3'
          sx={{
            fontWeight: 700,
            color: theme.palette.grey[900],
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            mb: 2,
          }}
        >
          See What Candidates Are Searching For
        </Typography>
        <Typography
          variant='body1'
          sx={{
            color: theme.palette.grey[700],
            fontSize: '1.05rem',
            mb: 4,
            maxWidth: 720,
          }}
        >
          MyStethi reveals real-time candidate search activity. See what specialties, locations, and
          practice types clinicians are actively looking for — and get automatically matched with
          candidates who fit your positions.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4 }}>
          {CANDIDATE_SEARCHES.map(search => (
            <Chip
              key={search}
              label={search}
              variant='outlined'
              sx={{
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.dark,
                fontWeight: 500,
                fontSize: '0.875rem',
                py: 0.5,
                '&:hover': {
                  backgroundColor: `${theme.palette.primary.main}14`,
                },
              }}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant='body1' sx={{ color: theme.palette.grey[700] }}>
            Want to see this in action?
          </Typography>
          <Button
            variant='contained'
            size='large'
            onClick={onScheduleDemo}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              fontWeight: 600,
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              textTransform: 'none',
              borderRadius: '8px',
              boxShadow: theme.palette.shadows.colored.primary,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: theme.palette.shadows.colored.primaryHover,
                transform: 'translateY(-2px)',
              },
            }}
          >
            Schedule a Demo
          </Button>
        </Box>
      </Container>
    </Box>
  )
}

function HowItWorks({ theme }) {
  return (
    <Box sx={{ backgroundColor: theme.palette.background.dark, py: { xs: 6, md: 8 } }}>
      <Container maxWidth='lg'>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant='h3'
            sx={{
              fontWeight: 700,
              color: theme.palette.common.white,
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              mb: 2,
            }}
          >
            How It Works
          </Typography>
          <Typography
            variant='body1'
            sx={{
              color: 'rgba(255,255,255,0.7)',
              fontSize: '1.05rem',
            }}
          >
            Three steps to connecting with your next hire.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {HOW_IT_WORKS_STEPS.map((step, index) => (
            <Grid item xs={12} md={4} key={step.title}>
              <Card
                sx={{
                  backgroundColor: 'transparent',
                  border: `2px solid ${theme.palette.primary.main}`,
                  borderRadius: '16px',
                  color: theme.palette.common.white,
                  height: '100%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.palette.shadows.colored.secondary,
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant='overline'
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      display: 'block',
                      mb: 1,
                    }}
                  >
                    Step {index + 1}
                  </Typography>
                  <IconCircle icon={step.icon} theme={theme} />
                  <Typography variant='h6' sx={{ fontWeight: 700, mb: 1.5 }}>
                    {step.title}
                  </Typography>
                  <Typography variant='body1' sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 }}>
                    {step.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

function WhyMyStethi({ theme }) {
  return (
    <Box sx={{ backgroundColor: theme.palette.common.white, py: { xs: 6, md: 8 } }}>
      <Container maxWidth='lg'>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant='h3'
            sx={{
              fontWeight: 700,
              color: theme.palette.grey[900],
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              mb: 2,
            }}
          >
            Why MyStethi
          </Typography>
          <Typography
            variant='body1'
            sx={{
              color: theme.palette.grey[600],
              fontSize: '1.05rem',
            }}
          >
            What makes us different from traditional healthcare recruitment.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {WHY_MYSTETHI_FEATURES.map(feature => (
            <Grid item xs={12} sm={6} key={feature.title}>
              <Card
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.grey[200]}`,
                  borderRadius: '16px',
                  height: '100%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.palette.shadows.colored.primary,
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <IconCircle icon={feature.icon} theme={theme} />
                  <Typography
                    variant='h6'
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                      mb: 1.5,
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{
                      color: theme.palette.grey[600],
                      lineHeight: 1.6,
                    }}
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

function ExclusiveOffer() {
  const theme = useTheme()

  return (
    <Box sx={{ backgroundColor: theme.palette.background.paper, py: { xs: 6, md: 8 } }}>
      <Container maxWidth='lg'>
        <Box
          sx={{
            backgroundColor: theme.palette.primary.main,
            borderRadius: '20px',
            p: { xs: 4, md: 6 },
            textAlign: 'center',
            boxShadow: theme.palette.shadows.colored.primary,
          }}
        >
          <Typography
            variant='overline'
            sx={{
              color: 'rgba(255,255,255,0.85)',
              fontWeight: 700,
              letterSpacing: '0.15em',
              display: 'block',
              mb: 1,
            }}
          >
            Limited Time
          </Typography>
          <Typography
            variant='h3'
            sx={{
              fontWeight: 800,
              color: theme.palette.common.white,
              fontSize: { xs: '1.5rem', md: '2.25rem' },
              mb: 2,
            }}
          >
            Exclusive AAPPR Attendee Rate
          </Typography>
          <Typography
            variant='h5'
            sx={{
              color: 'rgba(255,255,255,0.9)',
              fontWeight: 500,
              fontSize: { xs: '1.1rem', md: '1.4rem' },
            }}
          >
            Ask us at Booth 525 for details
          </Typography>
        </Box>
      </Container>
    </Box>
  )
}

function ContactForm({ theme }) {
  const dispatch = useDispatch()
  const [values, setValues] = useState(INITIAL_FORM_VALUES)
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [submitError, setSubmitError] = useState(false)

  const handleChange = useCallback(
    name => e => {
      const value = e.target.value
      setValues(prev => ({ ...prev, [name]: value }))
      if (touched[name]) {
        const error = validateField(name, value)
        setErrors(prev => {
          const next = { ...prev }
          if (error) {
            next[name] = error
          } else {
            delete next[name]
          }
          return next
        })
      }
    },
    [touched],
  )

  const handleBlur = useCallback(
    name => () => {
      setTouched(prev => ({ ...prev, [name]: true }))
      const error = validateField(name, values[name])
      setErrors(prev => {
        const next = { ...prev }
        if (error) {
          next[name] = error
        } else {
          delete next[name]
        }
        return next
      })
    },
    [values],
  )

  const handleSubmit = useCallback(async () => {
    if (loading) return

    const allTouched = Object.keys(INITIAL_FORM_VALUES).reduce(
      (acc, key) => ({ ...acc, [key]: true }),
      {},
    )
    setTouched(allTouched)

    const newErrors = {}
    Object.keys(INITIAL_FORM_VALUES).forEach(name => {
      const error = validateField(name, values[name])
      if (error) newErrors[name] = error
    })
    setErrors(newErrors)

    if (Object.keys(newErrors).length === 0) {
      setLoading(true)
      setSubmitError(false)
      try {
        await dispatch(fetchCreateContactEmail({
          values: {
            ...values,
            message: `[Source: AAPPR 2026 Conference Page]\n\n${values.message}`,
          },
        }))
        setSubmitted(true)
      } catch {
        setSubmitError(true)
      } finally {
        setLoading(false)
      }
    }
  }, [values, dispatch, loading])

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': { borderColor: theme.palette.primary.main },
      '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.dark },
  }

  if (submitted) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
          p: { xs: 4, md: 6 },
          textAlign: 'center',
          border: `2px solid ${theme.palette.primary.main}`,
        }}
      >
        <Typography
          variant='h5'
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.dark,
            mb: 2,
          }}
        >
          Demo Requested!
        </Typography>
        <Typography variant='body1' sx={{ color: theme.palette.grey[700] }}>
          Thanks for your interest! We'll be in touch within 48 hours.
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: '16px',
        p: { xs: 3, md: 5 },
        border: `1px solid ${theme.palette.grey[200]}`,
        borderLeft: `4px solid ${theme.palette.primary.main}`,
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label='First Name'
            variant='outlined'
            value={values.firstName}
            onChange={handleChange('firstName')}
            onBlur={handleBlur('firstName')}
            error={!!errors.firstName}
            helperText={errors.firstName || ' '}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label='Last Name'
            variant='outlined'
            value={values.lastName}
            onChange={handleChange('lastName')}
            onBlur={handleBlur('lastName')}
            error={!!errors.lastName}
            helperText={errors.lastName || ' '}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label='Email'
            variant='outlined'
            type='email'
            value={values.email}
            onChange={handleChange('email')}
            onBlur={handleBlur('email')}
            error={!!errors.email}
            helperText={errors.email || ' '}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label='Organization'
            variant='outlined'
            value={values.organization}
            onChange={handleChange('organization')}
            onBlur={handleBlur('organization')}
            error={!!errors.organization}
            helperText={errors.organization || ' '}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label='Message (optional)'
            variant='outlined'
            multiline
            rows={4}
            value={values.message}
            onChange={handleChange('message')}
            onBlur={handleBlur('message')}
            helperText=' '
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            fullWidth
            variant='contained'
            size='large'
            disabled={loading}
            onClick={handleSubmit}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              fontWeight: 700,
              py: 1.5,
              fontSize: '1rem',
              textTransform: 'none',
              borderRadius: '8px',
              boxShadow: theme.palette.shadows.colored.primary,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: theme.palette.shadows.colored.primaryHover,
              },
            }}
          >
            {loading ? 'Sending...' : 'Request a Demo'}
          </Button>
          {submitError && (
            <Typography
              variant='body2'
              sx={{ color: theme.palette.error.main, mt: 1, textAlign: 'center' }}
            >
              Something went wrong. Please try again.
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  )
}

function FooterCta({ theme, onScheduleDemo }) {
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.dark,
        color: theme.palette.common.white,
        py: { xs: 6, md: 8 },
        textAlign: 'center',
      }}
    >
      <Container maxWidth='md'>
        <Typography
          variant='h4'
          sx={{
            fontWeight: 700,
            mb: 2,
            fontSize: { xs: '1.5rem', md: '2rem' },
          }}
        >
          At the conference?
        </Typography>
        <Typography
          variant='body1'
          sx={{
            color: 'rgba(255,255,255,0.8)',
            fontSize: '1.1rem',
            lineHeight: 1.7,
            mb: 4,
          }}
        >
          Visit Booth 525 for a live demo and to see active candidate searches in action!
        </Typography>
        <Button
          variant='outlined'
          size='large'
          onClick={onScheduleDemo}
          sx={{
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            fontWeight: 600,
            px: 4,
            py: 1.5,
            fontSize: '1rem',
            textTransform: 'none',
            borderRadius: '8px',
            borderWidth: 2,
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              borderColor: theme.palette.primary.main,
              borderWidth: 2,
              transform: 'translateY(-2px)',
            },
          }}
        >
          Schedule a Demo
        </Button>
      </Container>
    </Box>
  )
}

const AAPPR2026 = () => {
  const theme = useTheme()
  const formRef = useRef(null)

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth' })

  useEffect(() => {
    const originalTitle = document.title
    const metaDescription = document.querySelector('meta[name="description"]')
    const originalDescription = metaDescription?.content
    const createdMeta = !metaDescription

    document.title = 'AAPPR 2026 Advancing Connections | MyStethi — Booth 525'

    if (!metaDescription) {
      const newMeta = document.createElement('meta')
      newMeta.name = 'description'
      newMeta.content = 'Visit MyStethi at AAPPR Advancing Connections 2026, Booth 525 in Orlando, FL. Discover our candidate search intelligence platform connecting healthcare professionals with hospitals and health systems.'
      document.head.appendChild(newMeta)
    } else {
      metaDescription.content = 'Visit MyStethi at AAPPR Advancing Connections 2026, Booth 525 in Orlando, FL. Discover our candidate search intelligence platform connecting healthcare professionals with hospitals and health systems.'
    }

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Event",
      "name": "AAPPR Advancing Connections 2026",
      "startDate": "2026-04-13",
      "endDate": "2026-04-15",
      "location": {
        "@type": "Place",
        "name": "Orlando, FL",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Orlando",
          "addressRegion": "FL",
          "addressCountry": "US"
        }
      },
      "organizer": {
        "@type": "Organization",
        "name": "MyStethi",
        "url": "https://www.mystethi.com"
      }
    }

    let structuredDataScript = document.getElementById('aappr2026-structured-data')
    if (!structuredDataScript) {
      structuredDataScript = document.createElement('script')
      structuredDataScript.id = 'aappr2026-structured-data'
      structuredDataScript.type = 'application/ld+json'
      document.head.appendChild(structuredDataScript)
    }
    structuredDataScript.textContent = JSON.stringify(structuredData)

    return () => {
      document.title = originalTitle
      const currentMeta = document.querySelector('meta[name="description"]')
      if (currentMeta) {
        if (createdMeta) {
          document.head.removeChild(currentMeta)
        } else {
          currentMeta.content = originalDescription || ''
        }
      }
      const script = document.getElementById('aappr2026-structured-data')
      if (script) {
        document.head.removeChild(script)
      }
    }
  }, [])

  return (
    <Box>
      <HeroSection theme={theme} onScheduleDemo={scrollToForm} />
      <TreasureChestExplainer theme={theme} onScheduleDemo={scrollToForm} />
      <ScheduleDemo theme={theme} formRef={formRef} />
      <CandidateSearchIntelligence theme={theme} onScheduleDemo={scrollToForm} />
      <HowItWorks theme={theme} />
      <WhyMyStethi theme={theme} />
      <ExclusiveOffer />
      <FooterCta theme={theme} onScheduleDemo={scrollToForm} />
    </Box>
  )
}

export default AAPPR2026
